import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton, type WButtonProps } from "@/components/button/WButton.tsx";
import type { ReactNode } from "react";

export interface WToolButtonProps extends Omit<WButtonProps, "ariaLabel" | "label" | "children"> {
    label: ReactNode;
    /** Compact, icon-only view for use in table cells */
    inTable?: boolean;
    /** Show the text label in {@link inTable} view (defaults to `true` when there is no icon) */
    showLabel?: boolean;
}

/**
 * Button to use in toolbars or table action columns.
 *
 * NB: The {@link WButton} `variant` and `size` props are overridden when `inTable` is `true`.
 */
export function WToolButton({ inTable, icon, showLabel = icon == null, label, ...props }: WToolButtonProps) {
    // TODO: Integrate the tooltip feature into `WButton` itself?
    const textLabel = typeof label === "string" ? label : undefined;

    if (inTable) {
        const button = (
            <WButton color="primary" ariaLabel={textLabel} icon={icon} {...props} variant="ghost" size="xs">
                {showLabel ? label : undefined}
            </WButton>
        );

        return showLabel ? button : <WTooltip text={label}>{button}</WTooltip>;
    } else {
        return (
            <WButton color="primary" variant="ghost" size="bar" icon={icon} {...props}>
                {label}
            </WButton>
        );
    }
}

export type WModalButtonProps = Omit<WToolButtonProps, "action">;

/** Button for triggering a modal.
 * @see {@link WToolButton}
 */
export function WModalButton(props: WModalButtonProps) {
    return <WToolButton action={undefined} {...props} />;
}

export interface WTooltipButtonProps extends WButtonProps {
    title: string;
}

export function WTooltipButton({ title, ...props }: WTooltipButtonProps) {
    return (
        <WTooltip text={title}>
            <WButton ariaLabel={title} {...props} />
        </WTooltip>
    );
}
